<template>
    <div v-if="show" class="page-link-modal-overlay" @click.self="close">
        <div class="page-link-modal">
            <div class="page-link-modal-header">
                <span>Link to a page</span>
                <button type="button" class="btn-close-x" @click="close">&times;</button>
            </div>
            <div class="page-link-modal-tabs">
                <button v-for="s in availableSections" :key="s.key" type="button" class="page-link-tab" :class="{ active: activeSection === s.key }" @click="activate(s.key)">{{ s.label }}</button>
            </div>
            <div class="page-link-modal-body">
                <input v-model="search" type="text" class="form-control form-control-sm mb-2" placeholder="Search..." />
                <div v-if="states[activeSection] === 'loading'" class="text-muted small p-2">Loading…</div>
                <div v-else-if="states[activeSection] === 'error'" class="text-danger small p-2">Failed to load</div>
                <div v-else class="page-link-list">
                    <button v-for="p in filtered" :key="p.entity + '-' + p.id" type="button" class="page-link-item" @click="select(p)">
                        <span class="page-link-title">{{ p.title }}</span>
                        <span class="page-link-url">{{ p.path }}</span>
                    </button>
                    <div v-if="!filtered.length" class="text-muted small p-2">No results</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import wordpressMixin from "@/mixins/wordpressMixin";
import cms6Mixin from "@/mixins/cms6Mixin";
import pageBuilderMixin from "@/mixins/pageBuilderMixin";
import siteMixin from "@/mixins/siteMixin";

// Mirror PageBuilder.vue's site-aware mixin selection so the entity fetchers / siteUrl resolve.
let site = localStorage.getObject("site");
let mixins = [siteMixin];
if (site && site.type === "wordpress") {
    mixins = [wordpressMixin, pageBuilderMixin, siteMixin];
} else if (site && site.type === "cms6") {
    mixins = [cms6Mixin, pageBuilderMixin, siteMixin];
}

// Entities the Juniper PHP resolver (PBHelper::resolveEntityPaths) understands. We only
// surface a section when its list fetcher exists on the active site type.
const SECTIONS = [
    { key: "pages", label: "Pages", entity: "Page", method: "getPages", prefix: "" },
    { key: "archives", label: "Predefined Pages", kind: "archive" },
    { key: "departments", label: "Departments", entity: "Department", method: "getDepartments", prefix: "departments" },
    { key: "news", label: "News", entity: "NewsNotice", method: "getNews", prefix: "news" },
    { key: "faqs", label: "FAQs", entity: "Faq", method: "getFaqs", prefix: "faqs" },
    { key: "business", label: "Business", entity: "BusinessDirectory", method: "getBusinessDirectories", prefix: "business" },
];

// Predefined archive/index pages — resolved by PBHelper::replaceArchiveTokens to {##archive:KEY##}.
// No API list (fixed set); paths shown are the defaults (real prefix comes from options on render).
const ARCHIVES = [
    { key: "NewsNotice", title: "News", path: "/news" },
    { key: "Event", title: "Events", path: "/events" },
    { key: "Meeting", title: "Meetings", path: "/meetings" },
    { key: "Faq", title: "FAQs", path: "/faqs" },
    { key: "Department", title: "Departments", path: "/departments" },
    { key: "BusinessDirectory", title: "Business Directory", path: "/business" },
    { key: "Post", title: "Blog Posts", path: "/posts" },
    { key: "Subscriber", title: "Subscribe", path: "/subscribe" },
    { key: "Search", title: "Search", path: "/search" },
];

export default {
    name: "PageLinkModal",
    emits: ["select"],
    data() {
        return { show: false, search: "", activeSection: "pages", items: {}, states: {} };
    },
    computed: {
        availableSections() {
            return SECTIONS.filter((s) => s.kind === "archive" || typeof this[s.method] === "function");
        },
        filtered() {
            const list = this.items[this.activeSection] || [];
            const q = this.search.trim().toLowerCase();
            if (!q) return list;
            return list.filter((p) => (p.title || "").toLowerCase().includes(q) || (p.path || "").toLowerCase().includes(q));
        },
    },
    methods: {
        async showModal() {
            this.show = true;
            if (!this.availableSections.find((s) => s.key === this.activeSection)) {
                this.activeSection = (this.availableSections[0] || {}).key || "pages";
            }
            await this.loadSection(this.activeSection);
        },
        close() {
            this.show = false;
        },
        activate(key) {
            this.activeSection = key;
            this.search = "";
            this.loadSection(key);
        },
        sectionByEntity(entity) {
            return SECTIONS.find((s) => s.entity === entity);
        },
        async rawFetch(method) {
            // each fetcher has its own arg order; call explicitly
            if (method === "getPages") return this.getPages(1, 100, "asc", "title", "");
            if (method === "getNews") return this.getNews(1, 100, "desc", "date", "");
            if (method === "getDepartments") return this.getDepartments(null, "", 1, 100);
            if (method === "getFaqs") return this.getFaqs(1, 100);
            if (method === "getBusinessDirectories") return this.getBusinessDirectories();
            return null;
        },
        mapItem(section, p) {
            if (!p || (p.id === undefined && p.id !== 0)) return null;
            const title = (p.title && (p.title.rendered ?? p.title)) || p.name || p.question || p.post_title || p.slug || "#" + p.id;
            let path = "";
            if (p.slug) path = p.slug_prefix ? `/${p.slug_prefix}${p.slug}` : section.prefix ? `/${section.prefix}/${p.slug}` : `/${p.slug}`;
            return { entity: section.entity, id: p.id, title, path };
        },
        async loadSection(key) {
            if (this.states[key] === "loading" || this.states[key] === "loaded") return;
            if (key === "archives") {
                this.items.archives = ARCHIVES.map((a) => ({ kind: "archive", key: a.key, title: a.title, path: a.path }));
                this.states.archives = "loaded";
                return;
            }
            const section = SECTIONS.find((s) => s.key === key);
            if (!section || typeof this[section.method] !== "function") return;
            this.states[key] = "loading";
            try {
                const res = await this.rawFetch(section.method);
                // each endpoint wraps its list differently: pages/departments/business → array
                // in res.data; news → res.data.post_data; faqs → res.data.faq_data
                const raw = res && res.data;
                const list = Array.isArray(raw) ? raw : (raw && (raw.post_data || raw.faq_data || raw.data || raw.items || raw.results)) || [];
                this.items[key] = list.map((p) => this.mapItem(section, p)).filter(Boolean);
                this.states[key] = "loaded";
            } catch (e) {
                console.error(`PageLinkModal: failed to load ${key}`, e);
                this.states[key] = "error";
            }
        },
        select(item) {
            this.$emit("select", item);
            this.close();
        },
        // resolve a saved token's display info ({title, path}); loads its section if needed
        async resolveToken(entity, id) {
            const section = this.sectionByEntity(entity);
            if (!section || typeof this[section.method] !== "function") return null;
            await this.loadSection(section.key);
            return (this.items[section.key] || []).find((p) => String(p.id) === String(id)) || null;
        },
        // grouped list for TinyMCE's native Insert/Edit Link dialog (link_list): one submenu
        // per section, each leaf value is the {##entity:Entity:id##} token.
        async getLinkList() {
            // sequential, NOT Promise.all: each fetcher's prepareForFetch aborts the shared
            // AbortController, so parallel calls would cancel each other (→ empty list)
            for (const s of this.availableSections) {
                await this.loadSection(s.key);
            }
            const groups = [];
            this.availableSections.forEach((s) => {
                const items = (this.items[s.key] || []).map((p) => ({
                    title: p.path ? `${p.title} — ${p.path}` : p.title,
                    value: p.kind === "archive" ? `{##archive:${p.key}##}` : `{##entity:${p.entity}:${p.id}##}`,
                }));
                if (items.length) groups.push({ title: s.label, menu: items });
            });
            return groups;
        },
    },
    mixins: mixins,
};
</script>

<style scoped>
.page-link-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1060;
    display: flex;
    align-items: center;
    justify-content: center;
}
.page-link-modal {
    background: #fff;
    border-radius: 8px;
    width: 520px;
    max-width: 92vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.3);
}
.page-link-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    font-weight: 600;
}
.btn-close-x {
    border: none;
    background: transparent;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
}
.page-link-modal-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 8px 16px 0;
}
.page-link-tab {
    border: none;
    background: #e6eaed;
    color: #555;
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 4px;
    cursor: pointer;
}
.page-link-tab.active {
    background: #2563eb;
    color: #fff;
}
.page-link-modal-body {
    padding: 12px 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}
.page-link-list {
    overflow-y: auto;
}
.page-link-item {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    border: none;
    background: transparent;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
}
.page-link-item:hover {
    background: #f3f4f6;
}
.page-link-title {
    font-size: 13px;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.page-link-url {
    font-size: 12px;
    color: #9ca3af;
    max-width: 45%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
